import _ from "lodash";
import {
  action,
  computed,
  observable,
  observe,
  when,
  makeObservable,
} from "mobx";
import config from "../../config";
import {
  getPatientConfig,
  getPatients,
  getSearchConfig,
} from "../../serviceCalls";
import { MODULE_ID, MODULE_NAME } from "../../shared/moduleIds";
import { MRN_STATE } from "../../shared/Patient/MrnState";
import { GROUP_SORT_ACCESSORS, patientSort } from "./PatientSort";
import { alphaNumericCompareBy } from "../SnippetReport/SnippetReportStore";
import { transformPatient } from "../../entities/Patient";

const moduleLabels = [
  "Communication",
  "Cardiology",
  "EMR",
  "Patient Monitoring",
  "Outpatient",
  "OB",
  "Reports",
];
const _groupSortOptions = {
  //PM
  3: [
    "NONE",
    "UNIT",
    "LAST_FIRST",
    "FIRST_LAST",
    "GENDER",
    "AGE",
    "DOB",
    "MRN",
    "BED",
    "ACTIVE_INACTIVE",
  ],
  //EMR
  2: [
    "NONE",
    "UNIT",
    "LAST_FIRST",
    "FIRST_LAST",
    "GENDER",
    "AGE",
    "DOB",
    "MRN",
    "BED",
  ],
  //OB
  5: [
    "NONE",
    "UNIT",
    "LAST_FIRST",
    "FIRST_LAST",
    "DILATATION",
    "EFFACEMENT",
    "STATION",
    "PHYSICIAN",
    "NURSE",
    "MEMBRANE_STATUS",
    "GRAVIDA",
    "PARA",
    "ROOM",
  ],
  //CARDIO
  // '1' : ['NONE', 'LAST_FIRST', 'FIRST_LAST', 'GENDER', 'AGE', 'DATETIME', 'DIAGNOSIS', 'ECG_COUNT']
};
const groupSortOptionLabels = {
  DOB: "DOB",
  MRN: "MRN",
  DATETIME: "Date",
  ECG_COUNT: "ECG Count",
  LAST_FIRST: "Last Name",
  FIRST_LAST: "First Name",
};

const moduleKeys = {
  1: "cardio",
  2: "emr",
  3: "pm",
  5: "ob",
};

const EMPTY_BED_PATENT = {
  age: "",
  asmpid: "",
  dateOfBirth: "",
  firstName: "",
  gender: "",
  lastActive: "",
  lastName: "",
  moduleSpecificData: [],
  mrn: "",
  notificationData: null,
  race: null,
  status: "",
  isEmptyBed: true,
};

export default class PatientStore {
  _patients = [];
  patient = undefined;
  _groups = [];
  lists = [];
  selectedListOption = { label: "", value: {} };
  loading;
  searchConfig;
  searchFormKey = 0;
  multiPatientNotify;
  groupByOptions = [];
  sort1Options = [];
  sort2Options = [];
  groupByValue;
  sort1Value;
  sort2Value;
  groupByInverted;
  sort1Inverted;
  sort2Inverted;
  searchQuery = "";
  noSearchRequirements;
  loadingConfig;
  singleColumn = true;
  hideCompleted;
  _hiddenUnits = [];
  numPatientsHiddenBySearchQuery = 0;
  modalLoading = false;
  tempHiddenUnits = [];
  patientItemTextModel;

  get hiddenUnits() {
    if (!this.unitFilterEnabled) return [];
    let hiddenUnits = [];
    const _filterUnits = _.get(
      this.appStore.prefs,
      `userPreferences.client.${moduleKeys[this.moduleId]}.unitFilter`,
    );
    if (_filterUnits) {
      hiddenUnits = Object.keys(_filterUnits).filter(
        (key) => _filterUnits[key] === false,
      );
    }
    return hiddenUnits;
  }
  set hiddenUnits(hiddenUnits) {
    const unitFilterSettings =
      this.filterUnits &&
      this.filterUnits.reduce((acc, u) => {
        acc[u] = !hiddenUnits.includes(u);
        return acc;
      }, {});

    this.appStore.setPref(
      `${moduleKeys[this.moduleId]}.unitFilter`,
      unitFilterSettings,
    );
  }

  set tempHiddenUnits(hiddenUnits) {
    this._tempHiddenUnits = hiddenUnits;
  }

  set modalLoading(loading) {
    this._modalLoading = loading;
  }

  get prefs() {
    return this.appStore.prefs;
  }
  set prefs(prefs) {
    this.appStore.setPrefs(prefs);
  }

  get isGroupSortSet() {
    return !!(
      _.get(this.groupByValue, "value") ||
      _.get(this.sort1Value, "value") ||
      _.get(this.sort2Value, "value")
    );
  }
  get groupSortOptions() {
    if (this.isWorklist) {
      return [];
    }
    return _groupSortOptions[this.moduleId] || [];
  }

  get unitFilterEnabled() {
    return (
      this.filterUnits &&
      (this.moduleId.toString() === MODULE_ID.PATIENT_MONITORING.toString() ||
        this.isOB) &&
      this.appStore.prefsFetched &&
      !this.isCustomBedList
    );
  }
  get unitFilterActive() {
    return !!this.hiddenUnits.length;
  }
  get filterUnits() {
    return _.get(this.selectedListOption, "value.config.filterUnits");
  }
  get selectedList() {
    return this.selectedListOption?.value;
  }

  constructor({ listIndex, appStore, history } = {}) {
    makeObservable(this, {
      filteredPatients: computed,
      filterResult: computed,
      _patients: observable,
      patients: computed,
      patient: observable,
      _groups: observable,
      lists: observable,
      selectedListOption: observable,
      loading: observable,
      searchConfig: observable,
      searchFormKey: observable,
      multiPatientNotify: observable,
      groupByOptions: observable,
      sort1Options: observable,
      sort2Options: observable,
      groupByValue: observable,
      sort1Value: observable,
      sort2Value: observable,
      groupByInverted: observable,
      sort1Inverted: observable,
      sort2Inverted: observable,
      searchQuery: observable,
      noSearchRequirements: observable,
      loadingConfig: observable,
      singleColumn: observable,
      hideCompleted: observable,
      _hiddenUnits: observable,
      numPatientsHiddenBySearchQuery: observable,
      tempHiddenUnits: observable,
      modalLoading: observable,
      filterUnits: computed,
      getPatients: action,
      updateSelect: action,
      groups: computed,
      groupsWithPatients: computed,
      inverter: action,
      listChange: action,
      getSearchConfig: action,
      fetchBaseLineWorkListCount: action,
      updateBaselineCounts: action,
      patientItemTextModel: observable,
      baselineWorlist: computed,
    });

    this.listIndex = isNaN(listIndex)
      ? sessionStorage[this.listIndexKey]
      : listIndex;
    this.appStore = appStore;
    this.history = history;
    observe(this.selectedListOption, this.compileSortOptions);

    this.compileSortOptions = this.compileSortOptions.bind(this);
    this.restoreGroupSort = this.restoreGroupSort.bind(this);
    this.getSearchConfig = this.getSearchConfig.bind(this);
    this.getPatients = this.getPatients.bind(this);
    this.moduleSpecifics = this.moduleSpecifics.bind(this);

    this.fetchPatientConfig();
    this.$ready = (async () => {
      await when(() => !!this.appStore.prefs);
      await this.patientConfigPromise;
      groupSortOptionLabels.MRN =
        this.appStore.prefs?.userPreferences?.server?.pm?.mrnLabel || "MRN";
      this.compileSortOptions(this.selectedListOption);
      this.restoreGroupSort();
    })();
  }

  set searchParams(params) {
    const storageKey = `searchParams_${this.listIndex}`;
    if (params && Object.keys(params).length)
      sessionStorage[storageKey] = JSON.stringify(params);
    else {
      sessionStorage.removeItem(storageKey);
    }
  }
  get searchParams() {
    try {
      return JSON.parse(sessionStorage[`searchParams_${this.listIndex}`]);
    } catch (e) {
      return null;
      // no search saved.
    }
  }

  abortFetchPatients() {
    if (this.controller && !this.controller?.signal.aborted) {
      this.controller.abort();
      this.controller = undefined;
    }
  }

  async getPatients(parameters, quiet, isRefresh) {
    if (!parameters) return;
    this.abortFetchPatients();
    const controller = new AbortController();
    this.controller = controller;
    let fetchPatientsPromise;
    try {
      fetchPatientsPromise = this.fetchPatients(parameters, {
        quiet: quiet || isRefresh,
        controller: this.controller,
      });
      this.fetchPatientsPromise = fetchPatientsPromise;
      if (!(quiet || isRefresh)) {
        this.loading = fetchPatientsPromise;
        this._patients = [];
        this._groups = undefined;
      }

      const res = await fetchPatientsPromise;

      // transform logic
      const patients = this.transformPatients(res.patients);
      // end transform logic
      this.groups = res.groups;
      this.patients = patients;
      if (!this.isWorklist && !this.isCustomBedList) {
        this.filterMyPatients({ patients });
      }

      // Store params for refresh when navigating back.
      this.getPatientsParams = parameters;
      // Setup refresh polling cycle.
      if (!isRefresh) {
        window.clearInterval(this.refreshIntervalId);
        this.refreshIntervalId = window.setInterval(() => {
          if (this.selectedList.type === "search" && !this.searchParams)
            this.getPatientsParams = undefined;
          if (!this.getPatientsParams) return;
          this.getPatients(this.getPatientsParams, true, true);
        }, config?.pm?.pollingFrequencyInSeconds * 1000);
      }
    } catch (e) {
      console.error("getPatients failed", e);
    } finally {
      if (controller === this.controller) {
        this.controller = undefined;
      }
      if (this.loading === fetchPatientsPromise) {
        this.loading = false;
      }
    }
  }

  /**
   * Tranforms a single patient.
   * @param {Object} patient
   * @param p
   * @returns {patient}
   */
  transformPatient = (p) => {
    return transformPatient(p, {
      moduleId: this.moduleId,
      isBaselineWorklist: this.isBaselineWorklist,
    });
  };

  async fetchPatients(parameters, { quiet, controller }) {
    if (!parameters) return;
    const res = await getPatients(parameters, { quiet, controller });
    this.updatePatientCounts({ patients: res.patients, parameters });
    return res;
  }

  updatePatientCounts({ patients, parameters }) {
    this.updateMyPatients({ patients, parameters });
    this.updateBaselineCounts({ patients, parameters });
  }

  async fetchListCounts() {
    await this.appStore.prefsPromise;
    await this.patientConfigPromise;
    await this.fetchPatientsPromise;
    this.fetchMyPatientsCount();
    this.fetchMyPatientsCount({ ob: true });
    this.fetchBaseLineWorkListCount();
  }
  /**
   * fetches user-defined patient list,
   * called at initialization to prune list
   * @async
   * @returns {Promise<Array>} Patients
   */
  fetchMyPatientsCount = async ({ ob } = {}) => {
    const moduleId = ob ? MODULE_ID.OBSTETRICS : MODULE_ID.PATIENT_MONITORING;
    if (!this.lists.find((l) => l.moduleId === moduleId)) return;
    const prefKey = ob ? "ob" : "pm";
    const asmpids = await this.appStore.getPref(`${prefKey}.multiPatients`);
    if (!asmpids?.length) return [];
    if (this.updatedCounts.has(prefKey)) return;
    const res = await getPatients({ moduleId }, { quiet: true });
    return this.updateMyPatients({
      patients: res.patients,
      parameters: { moduleId },
    });
  };
  updatedCounts = new Set();
  updateMyPatients({ patients, parameters } = {}) {
    if (!patients) return;
    const isOb = _.isEqual(parameters, {
      moduleId: MODULE_ID.OBSTETRICS.toString(),
    });
    const isPm = _.isEqual(parameters, {
      moduleId: MODULE_ID.PATIENT_MONITORING.toString(),
    });
    if (!isOb && !isPm) return;
    const { moduleId } = parameters;
    const prefKey = isPm ? "pm" : "ob";
    const myPatients = this.filterMyPatients({ patients, moduleId });
    const multiPatientsAsmpids = myPatients.map((p) => p.asmpid);
    const asmpids = patients.map((p) => p.asmpid);
    // Save the new list, if it's different.
    if (_.xor(multiPatientsAsmpids, asmpids).length) {
      if (isOb) {
        this.appStore.setPref("ob.multiPatients", multiPatientsAsmpids);
      } else {
        this.appStore.setPref("pm.multiPatients", multiPatientsAsmpids);
      }
    }
    this.updatedCounts.add(prefKey);
    return myPatients;
  }
  filterMyPatients = ({
    patients = this.patients,
    moduleId = this.moduleId,
  } = {}) => {
    const isPm = Number(moduleId) === MODULE_ID.PATIENT_MONITORING;
    const isOb = Number(moduleId) === MODULE_ID.OBSTETRICS;
    if (isOb || isPm) {
      const asmpids =
        this.appStore.prefs?.userPreferences?.client?.[isOb ? "ob" : "pm"]
          ?.multiPatients || [];
      if (!asmpids.length) return [];
      const multiPatients = [...patients].filter((patient) =>
        asmpids.includes(patient.asmpid),
      );
      return multiPatients;
    }
  };

  async restorePatientList({ listIndex } = {}) {
    if (listIndex >= 0) this.listIndex = listIndex;
    const selectedList = this.lists[this.listIndex];
    const selectedListOption = this.listOptions.find(
      (o) => o.value === selectedList,
    );
    await this.listChange(selectedListOption);
  }

  get moduleId() {
    return this.selectedListOption?.value?.moduleId || "";
  }
  get isOB() {
    return this.moduleId.toString() === MODULE_ID.OBSTETRICS.toString();
  }

  get listOptions() {
    const options = [];
    this.lists.forEach((list, i) => {
      if (
        options.length === 0 ||
        +this.lists[i - 1].moduleId !== list.moduleId
      ) {
        options.push({
          label: moduleLabels[list.moduleId],
          disabled: true,
          value: moduleLabels[list.moduleId],
          moduleId: list.moduleId,
        });
      }
      options.push({
        label: list.title,
        value: list,
        moduleId: list.moduleId,
      });
    });
    return options;
  }

  isSelected(option) {
    if (!this.selectedListOption?.value) return false;
    const { title, moduleId } = this.selectedListOption.value;
    return (
      option.value === this.selectedListOption ||
      (option.label === title && option.moduleId === moduleId)
    );
  }

  getOptionByListIndex(selectedIndex) {
    return selectedIndex && selectedIndex !== "-1"
      ? this.listOptions.find((option) => {
          const selectedList = this.lists[selectedIndex];
          return (
            selectedList &&
            option.value &&
            selectedList.title === option.value.title &&
            selectedList.moduleId === option.value.moduleId
          );
        })
      : null;
  }

  compileSortOptions() {
    const options = this.groupSortOptions.map((option) => ({
      label:
        groupSortOptionLabels[option] ||
        option
          .split(/[^a-z]/gi)
          .map(_.capitalize)
          .join(" "),
      value: option,
    }));
    ["groupByOptions", "sort1Options", "sort2Options"].forEach(
      (attr) => (this[attr] = [...options]),
    );
  }

  updateSelect(fieldName) {
    return (selection) => {
      this[`${fieldName}Value`] = selection;
    };
  }

  get isCustomBedList() {
    return this.selectedListOption?.value?.isCustomBedList;
  }

  /**
   * The transforms the patients on their way in.
   * @param _patients
   * @returns {*}
   */
  transformPatients(_patients) {
    let patients = _patients;
    patients = patients.map(this.transformPatient);
    if (this.isCustomBedList) {
      patients = this.compileBedList(patients, this.selectedListOption.value);
    }
    return patients;
  }

  sortPatients(patients) {
    if (!this.isCustomBedList) {
      if (
        this.moduleId.toString() === MODULE_ID.PATIENT_MONITORING.toString() &&
        !this.isGroupSortSet &&
        !this.isWorklist
      ) {
        // We're on MODULE_ID.PM, so sort by BED in ascending order
        patients = patientSort(patients, "BED", this.selectedListOption, false);
      }
      const sort2 = this?.sort2Value?.value;
      const sort1 = this?.sort1Value?.value;

      if (sort2) {
        patients = patientSort(
          patients,
          sort2,
          this.selectedListOption,
          this.sort2Inverted,
        );
      }
      if (sort1) {
        patients = patientSort(
          patients,
          sort1,
          this.selectedListOption,
          this.sort1Inverted,
        );
      }
    }
    return patients;
  }

  get patients() {
    const patients = this.filteredPatients;
    return this.sortPatients(patients);
  }

  compileBedList(patients, list) {
    return list?.customBeds.map((bed) => {
      const patientInBed = patients.find((p) => {
        return (
          p.moduleSpecifics?.unit === bed.unit &&
          p.moduleSpecifics?.location === bed.location
        );
      });
      if (patientInBed) return patientInBed;
      // else construct a "fake" patient
      const moduleSpecifics = {
        ...bed,
        moduleId: this.selectedListOption.moduleId,
      };
      return {
        ...EMPTY_BED_PATENT,
        asmpid: `EMPTY_BED-${bed.unit}-${bed.location}`,
        isEmptyBed: true,
        moduleSpecificData: [moduleSpecifics],
        moduleSpecifics,
      };
    });
  }

  set patients(_patients) {
    this._patients = _patients;
  }

  get groups() {
    if (this.isSurveil) return null;
    const groupBy = this.groupByValue?.value;
    if (this.isCustomBedList) return null;
    if (this.isBaselineWorklist && !this._groups) {
      return _.uniq(
        this._patients.map((patient) => patient.moduleSpecifics?.unit),
      )
        .sort(alphaNumericCompareBy())
        .map((unit) => ({
          groupId: unit,
          label: unit,
        }));
    } else if (groupBy) {
      let groups = _.uniq(
        this._patients &&
          this._patients.map((patient) =>
            this._getPatientSortProperty({
              patient,
              sortOption: groupBy,
            }),
          ),
      ).sort(alphaNumericCompareBy());

      if (groups.includes("{Discharged}")) {
        const dischargedIndex = groups.findIndex(
          (group) => group === "{Discharged}",
        );
        groups.splice(dischargedIndex, 1);
        groups.push("{Discharged}");
      }

      if (this.isOB) {
        groups = transformOBGroups({ groups, groupBy });
      }

      groups = groups.map((groupName) => ({
        label: groupName,
        sublabel: "",
      }));
      if (this.groupByInverted) {
        groups = groups.reverse();
      }
      return groups;
    }
    // default, groups from server.
    return this._groups;
  }
  set groups(_groups) {
    this._groups = _groups;
  }

  get groupsWithPatients() {
    let patients = this.patients;
    patients = this.filterCompletedSnippets(patients);
    return this.groups?.map((group) => {
      const groupPatients = this.groupPatients(group, patients);
      return {
        ...group,
        patients: groupPatients,
      };
    });
  }

  get filteredPatients() {
    return this.filterResult.patients;
  }
  get filterResult() {
    return this.filterPatients(this._patients, true);
  }

  filterPatients(_patients, withCounts) {
    let patients = _patients;
    /* #region Mandatory Filters */
    if (this.isMyPatients) {
      patients = this.filterMyPatients({ patients });
    }
    // AS1-5024 Filter out patients with mrnState of 'FixedMRN' when we are on
    // the snippet or baseline worklist.
    if (this.isWorklist) {
      patients = patients.filter((p) => p.mrnState !== MRN_STATE.FIXED);
    }
    /* #endregion */
    const total = patients.length;

    /* #region Optional Filters */
    patients = this.filterByUnits(patients);
    const totalForUnits = patients.length;
    patients = this.textFilter(patients);
    /* #endregion */

    if (withCounts)
      return {
        total,
        totalForUnits,
        patients,
      };
    return patients;
  }
  filterByUnits(patients) {
    if (!this.unitFilterEnabled) return patients;
    return patients.filter(
      (p) =>
        !_.includes(
          this.hiddenUnits.map((u) => u.toLowerCase()),
          _.get(this.moduleSpecifics(p), "unit", "").toLowerCase(),
        ),
    );
  }
  filterCompletedSnippets = (patients) => {
    if (this.isSnippetWorklist && this.hideCompleted) {
      return patients.filter(
        (patient) => !patient.moduleSpecifics?.snippetWorklistItemUpdated,
      );
    }
    return patients;
  };
  textFilter(patients) {
    if (!this.patientItemTextModel || !this.searchQuery) return patients;
    const terms = this.searchQuery.toLowerCase().split(/\s+/);
    return patients.filter((patient) => {
      const textModel = this.patientItemTextModel(patient);
      const values = Object.values(textModel)
        .map((v) => v?.toLowerCase())
        .filter(Boolean);
      return terms.every((term) =>
        values.find((value) => value.includes(term)),
      );
    });
  }

  get isMyPatients() {
    return this.checkIfMyPatients(this.selectedListOption);
  }
  checkIfMyPatients(selectedListOption) {
    return selectedListOption?.value?.type?.includes("multi_patient");
  }

  inverter(fieldName) {
    return () => {
      this[`${fieldName}Inverted`] = !this[`${fieldName}Inverted`];
    };
  }

  groupPatients(group, patients = []) {
    let groupPatients;
    if (this.groupByValue?.value) {
      groupPatients = patients.filter(
        (patient) =>
          group.label ===
          this._getPatientSortProperty({
            patient,
            sortOption: this.groupByValue.value,
          }),
      );
    } else {
      groupPatients = patients.filter(
        (patient) => patient.groupId === group.groupId,
      );
    }
    return groupPatients;
  }

  _getPatientSortProperty({ patient, sortOption }) {
    if (GROUP_SORT_ACCESSORS[sortOption]) {
      sortOption = GROUP_SORT_ACCESSORS[sortOption];
    }
    if (typeof sortOption === "function") {
      return sortOption(patient);
    } else {
      const key = _.camelCase(sortOption);
      const moduleSpecificData = _.get(
        this.moduleSpecifics(patient),
        key,
        _.get(patient, key),
      );
      if (moduleSpecificData === null || moduleSpecificData === undefined)
        return "--";
      return moduleSpecificData;
    }
  }

  moduleSpecifics(patient) {
    return patient?.moduleSpecificData?.find(
      (spec) =>
        spec.moduleId.toString() ===
        String(this.selectedListOption.value.moduleId),
    );
  }

  saveGroupSort() {
    if (this.groupSortDisabled) return;
    const groupSort = {
      groupBy: this.groupByValue?.value,
      sortBy1: this.sort1Value?.value,
      sortBy2: this.sort2Value?.value,
      groupByInvert: this.groupByInverted,
      sortBy1Invert: this.sort1Inverted,
      sortBy2Invert: this.sort2Inverted,
    };
    const moduleKey = moduleKeys[this.moduleId];
    this.appStore.setPref(`${moduleKey}.groupSort`, groupSort);
  }

  get groupSortActive() {
    return !_.isEqual(
      _.pickBy(this.groupSort, Boolean),
      this.defaultGroupSort(),
    );
  }
  resetGroupSort() {
    this.restoreGroupSort({ fromPrefs: false });
  }
  backupGroupSort() {
    this.groupSortBackup = {
      groupByValue: this.groupByValue,
      sort1Value: this.sort1Value,
      sort2Value: this.sort2Value,
      groupByInverted: this.groupByInverted,
      sort1Inverted: this.sort1Inverted,
      sort2Inverted: this.sort2Inverted,
    };
  }

  get groupSort() {
    const groupSort =
      this.prefs?.userPreferences?.client?.[moduleKeys[this.moduleId]]
        ?.groupSort;
    if (!groupSort || !Object.values(groupSort).filter(Boolean)) {
      return this.defaultGroupSort();
    }
    return groupSort;
  }
  restoreGroupSort({ fromPrefs = true } = {}) {
    let groupSort = fromPrefs ? this.groupSort : this.defaultGroupSort();

    this.groupByValue = this.groupByOptions.find(
      (option) => option.value === _.get(groupSort, "groupBy"),
    );
    this.sort1Value = this.sort1Options.find(
      (option) => option.value === _.get(groupSort, "sortBy1"),
    );
    if (this.isBaselineWorklist && !this.sort1Value)
      this.sort1Value = { value: "BED" };
    this.sort2Value = this.sort2Options.find(
      (option) => option.value === _.get(groupSort, "sortBy2"),
    );
    this.groupByInverted = _.get(groupSort, "groupByInvert");
    this.sort1Inverted = _.get(groupSort, "sortBy1Invert");
    this.sort2Inverted = _.get(groupSort, "sortBy2Invert");

    this.singleColumn = _.get(
      this.prefs,
      "userPreferences.client.pm.workList.singleColumn",
      true,
    );
    this.hideCompleted = _.get(
      this.prefs,
      "userPreferences.client.pm.workList.hideCompleted",
    );
  }

  groupSortDefaults = {
    PATIENT_MONITORING: {
      groupBy: "UNIT",
      sortBy1: "BED",
    },
    ELECTRONIC_MEDICAL_RECORD: {
      groupBy: "UNIT",
      sortBy1: "BED",
    },
    OBSTETRICS: {
      groupBy: "UNIT",
      sortBy1: "LAST_FIRST",
    },
  };
  defaultGroupSort() {
    return this.groupSortDefaults[MODULE_NAME[this.moduleId]] || {};
  }

  get groupSortDisabled() {
    return (
      !_.get(this.groupSortOptions, "length") ||
      this.isCustomBedList || // Disable for custom bed-lists.
      this.moduleId.toString() ===
        MODULE_ID.ELECTRONIC_MEDICAL_RECORD.toString() || // Disable for EMR specifically.
      this.isBaselineWorklist || // Disable for Baseline Worklist.
      this.isSnippetWorklist // Disable for Snippet Worklist
    );
  }

  async fetchPatientConfig() {
    this.loadingConfig = true;
    if (config.pm.snippetApprovalOverride) await this.appStore.prefsPromise;
    this.patientConfigPromise = getPatientConfig({
      snippetWorkListOverride:
        this.appStore.prefs?.userPreferences?.client?.pm
          ?.preventSnippetAutoApproval || undefined,
    })
      // filter out "Search" from OB and PM. (AS1-1469)
      // Also filter out OB and Communication (DEMO - 74) .
      .then((res) =>
        res.lists.filter(
          (list) =>
            +list.moduleId.toString() !== MODULE_ID.OBSTETRICS.toString() &&
            +list.moduleId.toString() !== MODULE_ID.GLOBAL.toString() &&
            !(
              list.type === "search" &&
              (+list.moduleId.toString() === MODULE_ID.OBSTETRICS.toString() ||
                +list.moduleId.toString() ===
                  MODULE_ID.PATIENT_MONITORING.toString())
            ),
        ),
      )
      // Add Snippets Reports list-o-lists option.
      .then((lists) =>
        config.snippetsReports
          ? [
              ...lists,
              {
                moduleId: MODULE_ID.COMMUNICATIONS,
                title: "Snippets Report",
                type: "snippets_reports",
                parameters: {},
                config: {},
              },
            ]
          : lists,
      )
      // insert "My Patients" option for user-defined list.
      .then((lists) => {
        const pmIndex = _.findIndex(
          lists,
          (list) =>
            list.moduleId.toString() ===
            MODULE_ID.PATIENT_MONITORING.toString(),
        );
        if (pmIndex !== -1) {
          lists.splice(pmIndex + 1, 0, {
            moduleId: MODULE_ID.PATIENT_MONITORING,
            title: "My Patients",
            type: "multi_patient",
            parameters: { moduleId: MODULE_ID.PATIENT_MONITORING.toString() },
            config: {},
          });
        }

        const obIndex = _.findIndex(
          lists,
          (list) =>
            list.moduleId.toString() === MODULE_ID.OBSTETRICS.toString(),
        );
        if (obIndex > -1) {
          lists.splice(obIndex + 1, 0, {
            moduleId: MODULE_ID.OBSTETRICS,
            title: "My Patients",
            type: "ob_multi_patient",
            parameters: { moduleId: MODULE_ID.OBSTETRICS.toString() },
            config: {},
          });
        }

        return lists;
      })
      // remove secure messaging if not configured.
      .then((lists) => {
        if (!config.enableSecureMessaging) {
          return lists.filter((list) => list.type !== "securemessaging");
        }
        return lists;
      })
      .then(this.addCustomBedLists)
      .then(async (lists) => {
        this.lists = lists;
        const moduleId = Number(sessionStorage.moduleId);
        const selectedIndex = this.listIndex;
        const optionToSelect =
          // If there's a list specified by the URL, use that.
          this.getOptionByListIndex(selectedIndex) ||
          // ...or else use the last one that was selected.
          this.getOptionByListIndex(sessionStorage.listIndex) ||
          // ...or find one with a matching moduleId.
          (!isNaN(moduleId) &&
            this.listOptions.find(
              (option) => Number(option.value.moduleId) === moduleId,
            )) ||
          // ...or find the option of type 'UNKNOWN' - all patients.
          this.listOptions.find((option) => option.value.type === "UNKNOWN") ||
          // ...or just select the first one as a last resort.
          this.listOptions.find((option) => !option.disabled);
        if (optionToSelect) {
          await this.appStore.prefsPromise;
          this.listChange(optionToSelect);
        }
        return this.lists;
      })
      .finally(() => (this.loadingConfig = false));
  }

  addCustomBedLists(lists) {
    return lists.reduce((total, curr, i) => {
      total.push(curr);
      let nextModuleId;
      if (lists.length > i + 1)
        nextModuleId = _.get(lists, `[${i + 1}].moduleId`, "").toString();
      if (
        curr.moduleId.toString() === MODULE_ID.PATIENT_MONITORING.toString() &&
        nextModuleId !== MODULE_ID.PATIENT_MONITORING.toString()
      ) {
        const census = _.find(lists, { type: "census" });
        if (census) {
          config.customBedLists.map((cbl, i) =>
            total.push({
              ...census,
              title: cbl.label,
              type: "custombedlist",
              isCustomBedList: true,
              customBedListIndex: i,
              customBeds: cbl.beds,
            }),
          );
        }
      }
      return total;
    }, []);
  }

  get listIndexKey() {
    return "listIndex";
  }
  async listChange(listOption) {
    this.getPatientsParams = null;
    this.abortFetchPatients();
    const index = this.lists.indexOf(listOption.value);
    // TODO: move snippet reports to subnav.
    if (listOption.value.type === "snippets_reports") {
      this.history?.push("/supervisor-report");
      this.patients = [];
      this.groups = null;
      // Prevent a cycle where the user gets trapped in snippetReports
      if (Number(this.listIndex) === index) this.listIndex = null;
      return;
    }
    // prevent reloading when switching between My Patients and base list.
    const loadInBackground =
      (this.isMyPatients || this.checkIfMyPatients(listOption)) &&
      _.isEqual(
        this.selectedListOption?.value.parameters,
        listOption.value.parameters,
      );
    sessionStorage[this.listIndexKey] = index;
    this.listIndex = index;
    this.selectedListOption = listOption;
    // Sync the patientStore in case we're in a mpv component.
    if (this.appStore.patientStore && this !== this.appStore.patientStore) {
      this.appStore.patientStore.selectedListOption = listOption;
    }

    this.compileSortOptions(listOption);
    this.searchConfig = undefined;
    if (
      listOption.value.type === "snippets_reports" ||
      listOption.value.type === "securemessaging"
    ) {
      this.patients = [];
      this.groups = null;
    } else if (
      listOption.value.type === "search" ||
      _.get(listOption.value.parameters, "listtype") === "search"
    ) {
      this.getSearchConfig(listOption.value.parameters.moduleId);
      this.groups = null;
      const { searchParams } = this;
      if (this.selectedList.type === "search" && searchParams) {
        // searchParams come from storage.
        this.getPatients({ ...searchParams, ...this.selectedList.parameters });
      }
    } else {
      if (loadInBackground) await this.fetchPatientsPromise;
      this.getPatients(
        listOption.value.parameters,
        loadInBackground,
        loadInBackground,
      );
    }

    if (!this.history?.location.pathname.includes("tele")) {
      this.history?.replace(`/patients${index !== -1 ? `/${index}` : ""}`);
    }
    if (!this.simpleSearchEnabled) {
      this.searchQuery = "";
    }

    if (_.get(listOption, "value.isCustomBedList")) {
      this.groupByValue = undefined;
      this.sort1Value = undefined;
      this.sort2Value = undefined;
      this.groupByInverted = undefined;
      this.sort1Inverted = undefined;
      this.sort2Inverted = undefined;
    } else {
      this.restoreGroupSort();
    }
  }

  async getSearchConfig(moduleId) {
    this.loading = true;
    this.patients = [];
    this.searchConfig = await getSearchConfig({
      asmpid: this.asmpid,
      moduleId,
    });
    this.loading = false;
  }

  get simpleSearchEnabled() {
    return (
      !this.isWorklist &&
      this.moduleId.toString() !== MODULE_ID.COMMUNICATIONS.toString() &&
      this.moduleId.toString() !== MODULE_ID.CARDIOLOGY.toString()
    );
  }

  get isSnippetWorklist() {
    return (
      _.get(this.selectedListOption, "value.type", "").toLowerCase() ===
      "snippetsworklist"
    );
  }

  static isBaselineWorklist(listOption) {
    return (listOption.value || listOption)?.type
      ?.toLowerCase()
      .match("baselineworklist");
  }
  get isBaselineWorklist() {
    return PatientStore.isBaselineWorklist(this.selectedListOption);
  }
  get isWorklist() {
    return this.isSnippetWorklist || this.isBaselineWorklist;
  }

  get multiViewEnabled() {
    return (
      this.appStore.hasMultiPatient &&
      (_.get(this.selectedListOption, "value.type") === "multi_patient" ||
        this.moduleId.toString() === MODULE_ID.OBSTETRICS.toString() ||
        (this.moduleId.toString() === MODULE_ID.PATIENT_MONITORING.toString() &&
          ["census", "unknown", "custombedlist"].includes(
            _.get(this.selectedListOption, "value.type"),
          )))
    );
  }

  get baselineWorlist() {
    return this.lists.find((list) => list.type.match("baselineworklist"));
  }
  fetchBaseLineWorkListCount() {
    if (this.isBaselineWorklist) return;
    if (this.updatedCounts.has("baselineworklist")) return;
    if (!this.baselineWorlist) return;
    this.fetchPatients(this.baselineWorlist.parameters, { quiet: true });
  }
  updateBaselineCounts({ patients, parameters }) {
    if (!this.baselineWorlist) return;
    if (_.isEqual(parameters, this.baselineWorlist.parameters)) {
      const realPatients = patients.filter(
        (patient) => patient.mrnState !== "MrnFixed",
      );
      this.baselineCount = realPatients.length;
      this.updatedCounts.add("baselineworklist");
    }
    const list = this.baselineWorlist;
    if (list.count !== this.baselineCount) {
      list.count = this.baselineCount;
      // force update if we changed the count.
      this.lists = [...this.lists];
    }
  }
}
function transformOBGroups({ groups, groupBy }) {
  // AS1-4973 & AS1-4975:
  // If numeric group values (i.e. effacement or station),
  // sort as numbers and reverse.
  let AscNumeric = groupBy === "PARA" || groupBy === "GRAVIDA";
  let DescNumeric =
    groupBy === "EFFACEMENT" ||
    groupBy === "STATION" ||
    groupBy === "DILATATION";
  let special = groupBy === "MEMBRANE_STATUS";

  let isDescNumericGroupValues =
    DescNumeric &&
    !groups.filter((group) => group !== "--").filter((group) => isNaN(group))
      .length;

  if (isDescNumericGroupValues) {
    const notDocumented = groups.findIndex((group) => group === "--");
    groups.splice(notDocumented, 1);
    groups.sort((a, b) => a - b);
    groups.reverse();
    groups.push("--");
  } else if (AscNumeric) {
    groups.sort((a, b) => a - b);
  } else if (special) {
    groups.reverse();
  }
  return groups;
}
